import { useMemo, useState } from 'react';
import { useCreateQrCode } from '../api/useCreateQrCode';
import {
  DEFAULT_CUSTOMIZATION,
  type CreateQrCodePayload,
  type QrCodeRecord,
  type QrCodeType,
  type QrCustomization,
} from '../api/types';
import { QrCustomizationPanel } from './QrCustomizationPanel';
import { QrPreview, downloadQr } from './QrPreview';
import { LogoUploader } from './LogoUploader';

interface Props {
  type: QrCodeType;
  onCancel: () => void;
  onCreated: (record: QrCodeRecord) => void;
}

const QR_BASE_URL =
  process.env.REACT_APP_QR_BASE_URL?.replace(/\/$/, '') ||
  'https://api.socialsparkapp.net/prod/qr';

const SLUG_RE = /^[a-z0-9-]{3,64}$/;

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function QrCodeCreate({ type, onCancel, onCreated }: Props) {
  const isCampaign = type === 'campaign';
  const { create, loading } = useCreateQrCode();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customization, setCustomization] = useState<QrCustomization>(DEFAULT_CUSTOMIZATION);
  const [logo, setLogo] = useState<{ s3_key: string; preview_url: string } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const qrUrl = effectiveSlug ? `${QR_BASE_URL}/${effectiveSlug}` : `${QR_BASE_URL}/preview`;

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'Name is required';
    else if (name.length > 200) errors.name = 'Name is too long (200 char max)';

    if (!effectiveSlug) errors.slug = 'Slug is required';
    else if (!SLUG_RE.test(effectiveSlug))
      errors.slug = 'Slug must be 3-64 chars, lowercase letters/digits/hyphens';

    if (description && description.length > 1000)
      errors.description = 'Description is too long (1000 char max)';

    if (isCampaign && startDate && endDate && startDate > endDate)
      errors.dates = 'Start date must be on or before end date';

    return errors;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload: CreateQrCodePayload = {
      campaign_id: effectiveSlug,
      type,
      campaign_name: name.trim(),
      customization,
    };
    if (description.trim()) payload.description = description.trim();
    if (isCampaign) {
      if (startDate) payload.start_date = startDate;
      if (endDate) payload.end_date = endDate;
    }
    if (logo) payload.logo_s3_key = logo.s3_key;

    const result = await create(payload);
    if (result.error) {
      if (result.error.code === 'slug_taken') {
        setFieldErrors((prev) => ({ ...prev, slug: 'This slug is already taken' }));
      } else {
        setSubmitError(result.error.message);
      }
      return;
    }
    if (result.data) onCreated(result.data);
  };

  const hasLogo = logo !== null;
  const previewLogo = logo?.preview_url;
  const downloadName = useMemo(
    () => effectiveSlug || 'qr-preview',
    [effectiveSlug],
  );

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-white">
            {isCampaign ? 'Create campaign' : 'Create permanent QR'}
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            {isCampaign
              ? 'Time-bound marketing QR. Customize the design and download a print-ready file.'
              : 'Always-on QR for placement at businesses. Customize the design and download a print-ready file.'}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-800 text-gray-300 hover:bg-gray-800/40"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-3 py-1.5 text-sm rounded-md bg-violet-600 text-white font-medium hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving…' : 'Save & generate'}
          </button>
        </div>
      </header>

      {submitError && (
        <div className="rounded-md border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-300">
          {submitError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6">
        <div className="space-y-5">
          <div>
            <Label>{isCampaign ? 'Campaign name' : 'Display name'}</Label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={isCampaign ? "e.g. St. Patrick's Bar Crawl" : 'e.g. Door sticker (standard)'}
              className={inputClass(fieldErrors.name)}
              maxLength={200}
            />
            {fieldErrors.name && <ErrorText>{fieldErrors.name}</ErrorText>}
          </div>

          <div>
            <Label>Slug</Label>
            <input
              value={effectiveSlug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
              }}
              placeholder="auto-generated from name"
              className={inputClass(fieldErrors.slug)}
              maxLength={64}
            />
            <div className="text-[11px] text-gray-500 mt-1.5 font-mono break-all">
              URL: <span className="text-gray-300">{qrUrl}</span>
              <span className="text-gray-500 ml-2">· cannot change after save</span>
            </div>
            {fieldErrors.slug && <ErrorText>{fieldErrors.slug}</ErrorText>}
          </div>

          {isCampaign && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start date (optional)</Label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={inputClass(fieldErrors.dates)}
                />
              </div>
              <div>
                <Label>End date (optional)</Label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={inputClass(fieldErrors.dates)}
                />
              </div>
              {fieldErrors.dates && (
                <div className="col-span-2">
                  <ErrorText>{fieldErrors.dates}</ErrorText>
                </div>
              )}
            </div>
          )}

          {!isCampaign && (
            <div>
              <Label>Description (optional)</Label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. 4x4 vinyl sticker placed at front entry"
                className={inputClass(fieldErrors.description)}
                maxLength={1000}
              />
              {fieldErrors.description && <ErrorText>{fieldErrors.description}</ErrorText>}
            </div>
          )}

          <div className="pt-2 border-t border-gray-800">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-3">
              Customize QR
            </div>
            <QrCustomizationPanel
              value={customization}
              onChange={setCustomization}
              hasLogo={hasLogo}
              logoSlot={
                <LogoUploader
                  logoUrl={previewLogo ?? null}
                  onChange={(next) => {
                    setLogo(next);
                    if (next && customization.qrOptions.errorCorrectionLevel !== 'H') {
                      setCustomization((prev) => ({
                        ...prev,
                        qrOptions: { errorCorrectionLevel: 'H' },
                      }));
                    }
                  }}
                />
              }
            />
          </div>
        </div>

        <div className="flex flex-col items-center gap-4 lg:sticky lg:top-6 self-start">
          <div className="bg-white rounded-xl p-4 shadow-lg">
            <QrPreview
              data={qrUrl}
              customization={customization}
              logoUrl={previewLogo}
              size={240}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => downloadQr(qrUrl, customization, previewLogo, downloadName, 'png')}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-800 text-gray-200 hover:bg-gray-800/50"
            >
              Download PNG
            </button>
            <button
              type="button"
              onClick={() => downloadQr(qrUrl, customization, previewLogo, downloadName, 'svg')}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-800 text-gray-200 hover:bg-gray-800/50"
            >
              Download SVG
            </button>
          </div>
          <div className="text-[11px] text-gray-500 text-center">
            Live preview · updates as you tweak
          </div>
        </div>
      </div>
    </form>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs uppercase tracking-wide text-gray-500 mb-1.5">
      {children}
    </div>
  );
}

function ErrorText({ children }: { children: React.ReactNode }) {
  return <div className="text-[11px] text-red-400 mt-1.5">{children}</div>;
}

function inputClass(error?: string): string {
  return [
    'w-full bg-gray-900 border rounded px-3 py-2 text-sm text-gray-100 placeholder:text-gray-600',
    'focus:outline-none focus:border-violet-500',
    error ? 'border-red-700' : 'border-gray-800',
  ].join(' ');
}
